import { Mp4BoxInfo } from "mp4box";
import { ValidationsObject, VALIDATIONS, EventUploadState, Action, ACTION_TYPE, UpdateError, CHECKSUM_MESSAGE } from "./eventUploadTypes";
import { TrackSample } from "../../declarations/mp4box";
import ChecksumWorker from "./checksum.worker";

export const MAX_KEYFRAME_INTERVAL = 3.2;
export const KB = 1024;
export const MB = KB * KB;
const GB = MB * KB;
export const MAX_DURATION_SEC = 21600; // 6 hours
export const MAX_FILE_SIZE = 15 * GB; // 15 GB
const MAX_FRAGMENT_MBPS = 24;
const AUDIO_SAMPLERATE = 48000;

export const validations: ValidationsObject = Object.freeze({
  [VALIDATIONS.BACK_END]: {
    message: 'Something went wrong with your upload. Please try again or report the problem if it persists.',
    invalid: false,
    key: 'back-end-error',
  },
  [VALIDATIONS.NETWORK_ERROR]: {
    message: 'Upload failed due to a connection issue. Please try again or report the problem if it persists.',
    invalid: false,
    key: 'back-end-error',
  },
  [VALIDATIONS.NOT_A_VIDEO]: {
    message: 'File must be a video file',
    invalid: false,
    key: 'not-a-video',
  },
  [VALIDATIONS.INVALID_FORMAT]: {
    message: 'File format must be H.264',
    invalid: false,
    key: 'bad-format',
  },
  [VALIDATIONS.INVALID_DIMENSIONS]: {
    message: 'File must have a 16:9 aspect ratio',
    invalid: false,
    key: 'invalid-dimensions',
  },
  [VALIDATIONS.BLANK_EVENT_NAME]: {
    message: 'Event Name cannot be blank',
    invalid: false,
    key: 'invalid-event-name',
  },
  [VALIDATIONS.BLANK_EVENT_PROFILE]: {
    message: 'Event Profile must be selected',
    invalid: false,
    key: 'invalid-event-profile',
  },
  [VALIDATIONS.NO_UPLOAD_FILE]: {
    message: 'Please select a file to upload',
    invalid: false,
    key: 'invalid-upload-selection',
  },
  [VALIDATIONS.FILE_SIZE_0]: {
    message: 'The file you tried to upload appears empty. Cancel and try again, or report the problem if it persists.',
    invalid: false,
    key: 'empty-file-size',
  },
  [VALIDATIONS.INVALID_FILE_TYPE]: {
    message: 'File must be an mp4 file',
    invalid: false,
    key: 'invalid-file-type',
  },
  [VALIDATIONS.INVALID_AUDIO_TYPE]: {
    message: 'Audio codec must be aac',
    invalid: false,
    key: 'invalid-audio-type',
  },
  [VALIDATIONS.MAX_DURATION]: {
    message: 'Max duration is 6 hours',
    invalid: false,
    key: 'invalid-max-duration',
  },
  [VALIDATIONS.MAX_FILE_SIZE]: {
    message: 'Max file size is 15 GB',
    invalid: false,
    key: 'invalid-max-file-size',
  },
  [VALIDATIONS.MAX_AUDIO_CHANNELS]: {
    message: 'File cannot exceed 2 audio channels',
    invalid: false,
    key: 'invalid-max-audio-channels',
  },
  [VALIDATIONS.MAX_AUDIO_BITRATE]: {
    message: 'Audio bitrate cannot exceed 192kbps',
    invalid: false,
    key: 'invalid-max-bit-rate',
  },
  [VALIDATIONS.MAX_VIDEO_CHANNELS]: {
    message: 'File has more than one video channel',
    invalid: false,
    key: 'invalid-max-video-channels',
  },
  [VALIDATIONS.MAX_VIDEO_BITRATE]: {
    message: 'Video bitrate cannot exceed 8 Mbps for 59.94/60 fps, or 6 Mbps for all other framerates',
    invalid: false,
    key: 'invalid-max-video-bitrate',
  },
  [VALIDATIONS.MAX_RESOLUTION]: {
    message: 'Max resolution is 1920 x 1080',
    invalid: false,
    key: 'invalid-max-resolution',
  },
  [VALIDATIONS.INVALID_FPS]: {
    message: 'Framerate must be constant and one of 24, 25, 30, or 60 fps (NTSC-interoperable frame rates are also supported).',
    invalid: false,
    key: 'invalid-max-fps',
  },
  [VALIDATIONS.MAX_GOP]: {
    message: 'Keyframe Interval is too large for sim-live or social destination streaming.  See Upload File Requirements for more information.',
    invalid: false,
    key: 'invalid-max-gop-interval',
  },
  [VALIDATIONS.INVALID_AUDIO_SAMPLERATE]: {
    message: 'Audio sample rate must be 48 kHz.',
    invalid: false,
    key: 'invalid_audio_samplerate'
  },
  [VALIDATIONS.NO_AUDIO]: {
    message: 'Upload file must contain audio.',
    invalid: false,
    key: 'invalid_no_audio'
  }
});

const areDefined = (...args: any[]) => [...args].every(x => x !== undefined && x !== null);

export const initialState: EventUploadState = Object.freeze({
  eventProfile: '',
  eventName: '',
  uploadProgress: 0,
  uploadTotal: 0,
  analyzingLoaded: 0,
  analyzingTotal: 0,
  profileList: [],
  errors: { ...validations },
  fileName: '',
  file: null,
  fileChecksum: null,
  fileChecksumProgress: 0,
  isUploading: false,
  uploadCanceled: false,
  uploadFinished: false,
  signedUrlResponse: null,
  gopSeconds: 0,
  showFileRequirements: false,
  isAnalysisRunning: false,
  isChecksumTaskRunning: false,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const readFile = (file: File): Promise<any> => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
};

export interface ChecksumTask {
  hash(): Promise<string | null>;
  cancel(): void;
}

class InternalChecksumTask implements ChecksumTask {
  constructor(private worker: Worker, private resultPromise: Promise<string | null>, private onCancel?: () => void) { }

  public hash(): Promise<string | null> {
    return this.resultPromise;
  }

  public cancel(): void {
    this.onCancel?.();
    this.worker.terminate();
  }
}

export function computeChecksum(blob: Blob, onProgress: (progressBytes: number) => void): ChecksumTask {
  const worker = new ChecksumWorker();
  let onCancel;
  const resultPromise = new Promise<string | null>((resolve, reject) => {
    worker.onerror = (e) => reject(e.error)
    onCancel = () => {
      console.log("Checksum task was cancelled");
      resolve(null);
    };
    worker.onmessage = ({ data: message }) => {
      switch (message.type) {
        case CHECKSUM_MESSAGE.PROGRESS:
          onProgress(message.payload);
          break;
        case CHECKSUM_MESSAGE.SUCCESS:
          resolve(message.payload);
          break;
        case CHECKSUM_MESSAGE.ERROR:
          reject(message.payload);
          break;
      }
    }
    worker.postMessage(blob);
  });

  return new InternalChecksumTask(worker, resultPromise, onCancel);
}

export async function sliceLargeBlob(blob: Blob, addToBuffer: (blob: Blob, pos: number) => Promise<void>): Promise<void> {
  const maxSliceBytes = 32 * MB;
  let currentValue = 0;

  if (blob.size > maxSliceBytes) {
    while (currentValue < blob.size || blob.size - currentValue > maxSliceBytes) {
      const smallBlob = blob.slice(currentValue, currentValue + maxSliceBytes + 1, 'video/mp4');
      await addToBuffer(smallBlob, currentValue)
      currentValue += maxSliceBytes + 1;
    }
  }

  // handle last remaining blob that was less than maxSliceBytes
  const smallBlob = blob.slice(currentValue, blob.size);
  await addToBuffer(smallBlob, currentValue);
}

export function reducer(state: EventUploadState, action: Action): EventUploadState {
  switch (action.type) {
    case ACTION_TYPE.PROFILE:
      return { ...state, eventProfile: action.payload }
    case ACTION_TYPE.NAME:
      return { ...state, eventName: action.payload }
    case ACTION_TYPE.UPLOAD_PROGRESS:
      return { ...state, uploadProgress: action.payload }
    case ACTION_TYPE.UPLOAD_TOTAL:
      return { ...state, uploadTotal: action.payload }
    case ACTION_TYPE.ANALYZING_LOADED:
      return { ...state, analyzingLoaded: action.payload }
    case ACTION_TYPE.ANALYZING_TOTAL:
      return { ...state, analyzingTotal: action.payload }
    case ACTION_TYPE.FILE:
      return { ...state, file: action.payload }
    case ACTION_TYPE.FILE_NAME:
      return { ...state, fileName: action.payload }
    case ACTION_TYPE.FILE_CHECKSUM:
      return { ...state, fileChecksum: action.payload };
    case ACTION_TYPE.FILE_CHECKSUM_PROGRESS:
      return { ...state, fileChecksumProgress: action.payload };
    case ACTION_TYPE.PROFILE_LIST:
      return { ...state, profileList: action.payload }
    case ACTION_TYPE.ERROR_LIST:
      return { ...state, errors: action.payload }
    case ACTION_TYPE.IS_UPLOADING:
      return { ...state, isUploading: action.payload }
    case ACTION_TYPE.UPLOAD_CANCELED:
      return { ...state, uploadCanceled: action.payload }
    case ACTION_TYPE.UPLOAD_FINISHED:
      return { ...state, uploadFinished: action.payload }
    case ACTION_TYPE.SIGNED_URL_RES:
      return { ...state, signedUrlResponse: action.payload }
    case ACTION_TYPE.GOP_SECONDS:
      return { ...state, gopSeconds: action.payload }
    case ACTION_TYPE.SHOW_FILE_REQUIREMENTS:
      return { ...state, showFileRequirements: action.payload }
    case ACTION_TYPE.SET_ANALYSIS_RUNNING:
      return { ...state, isAnalysisRunning: action.payload }
    case ACTION_TYPE.SET_CHECKSUM_TASK_RUNNING:
      return { ...state, isChecksumTaskRunning: action.payload }
    default:
      throw new Error("Invalid Action Type: " + action.type);
  }
}

function hasAudio(info: Mp4BoxInfo): boolean {
  if (!info?.audioTracks || !info.audioTracks.length) {
    console.log('No audio tracks found for this video upload');
    return false;
  }
  return true;
}

// validations
export function validateDuration(info: Mp4BoxInfo, updateError: UpdateError) {
  const [videoTrack] = info.videoTracks;
  const seconds = videoTrack.duration / videoTrack.timescale;
  if (seconds > MAX_DURATION_SEC) {
    updateError(VALIDATIONS.MAX_DURATION);
    return;
  }

  updateError(VALIDATIONS.MAX_DURATION, false);
}

export function validateFileSize(info: Mp4BoxInfo, updateError: UpdateError) {
  const [videoTrack] = info.videoTracks;

  if (videoTrack.size > MAX_FILE_SIZE) {
    updateError(VALIDATIONS.MAX_FILE_SIZE);
    return;
  }

  updateError(VALIDATIONS.MAX_FILE_SIZE, false);
}

export function validateVideoType(info: Mp4BoxInfo, updateError: UpdateError) {
  const supportedVideoTypes = ['avc1'];
  let supportedVideoType = false;

  for (const type of supportedVideoTypes) {
    if (!info?.videoTracks[0].codec) {
      continue;
    }

    if (info.videoTracks[0].codec.toLowerCase().indexOf(type) > -1) {
      supportedVideoType = true;
      break;
    }
  }

  if (!supportedVideoType) {
    updateError(VALIDATIONS.INVALID_FORMAT);
    return;
  }
  updateError(VALIDATIONS.INVALID_FORMAT, false);
}

export function validateAudioType(info: Mp4BoxInfo, updateError: UpdateError) {
  if (!hasAudio(info)) {
    return;
  }

  const [audioTrack] = info.audioTracks;

  if (/^mp4a.40/.test(audioTrack.codec) === false) {
    updateError(VALIDATIONS.INVALID_AUDIO_TYPE);
    return;
  }
  updateError(VALIDATIONS.INVALID_AUDIO_TYPE, false)
}

export function validateMaxAudioChannels(info: Mp4BoxInfo, updateError: UpdateError) {
  if (!hasAudio(info)) {
    return;
  }

  const [audioTrack] = info.audioTracks;
  console.log(`Max Audio Tracks: 2 -- Actual Audio Tracks: ${audioTrack.audio.channel_count}`);
  if (audioTrack.audio.channel_count > 2) {
    updateError(VALIDATIONS.MAX_AUDIO_CHANNELS);
    return;
  }
  updateError(VALIDATIONS.MAX_AUDIO_CHANNELS, false);
}

export function validateAudioBitrate(info: Mp4BoxInfo, updateError: UpdateError) {
  // allows max bit rate and some extra.
  // Technically max bit rate is 192kbps, but we check for 210
  if (!hasAudio(info)) {
    return;
  }

  const [audioTrack] = info.audioTracks;
  const bitrate = audioTrack.bitrate / KB;
  console.log(`Max Bitrate: 192kbps -- Actual Audio Bitrate: ${bitrate}kbps`);
  if (bitrate > 210) {
    updateError(VALIDATIONS.MAX_AUDIO_BITRATE);
    return;
  }

  updateError(VALIDATIONS.MAX_AUDIO_BITRATE, false);
}

export function validateMaxVideoChannels(info: Mp4BoxInfo, updateError: UpdateError) {
  console.log(`Max Video Channels: 1 -- Actual Video Channels: ${info.videoTracks.length}`);

  if (info.videoTracks && info.videoTracks.length > 1) {
    updateError(VALIDATIONS.MAX_VIDEO_CHANNELS);
    return;
  }
  updateError(VALIDATIONS.MAX_VIDEO_CHANNELS, false);
}

export function validateMaxVideoBitrate(info: Mp4BoxInfo, updateError: UpdateError) {
  if (info.videoTracks && info.videoTracks.length) {
    const [videoTrack] = info.videoTracks;
    const { bitrate, timescale, samples_duration: samplesDuration, nb_samples: nbSamples } = videoTrack;

    const bitrateMbps = bitrate / KB / KB;
    const fps = timescale / (samplesDuration / nbSamples);

    console.log(`Max Video Bitrate: 8Mbps (60fps only) / 6Mbps  -- Actual Video Bitrate: ${bitrateMbps}Mbps (${fps}fps)`);

    if (fps <= 30 && bitrateMbps > 6 || bitrateMbps > 8) {
      updateError(VALIDATIONS.MAX_VIDEO_BITRATE);
      return;
    }
  }
  updateError(VALIDATIONS.MAX_VIDEO_BITRATE, false);
}

export function validateMaxResolution(info: Mp4BoxInfo, updateError: UpdateError) {
  if (info.videoTracks && info.videoTracks.length) {
    const [videoTrack] = info.videoTracks;

    console.log(`Max resolution is 1920 x 1080 -- Actual Resolution: ${videoTrack.track_width} x ${videoTrack.track_height}`);

    if (videoTrack.track_height > 1080) {
      updateError(VALIDATIONS.MAX_RESOLUTION);
      return;
    }
  }

  updateError(VALIDATIONS.MAX_RESOLUTION, false);
}

// Our sample duration is found by taking sample_durations divided by nb_samples.
export function logSampleDuration(info: Mp4BoxInfo) {
  if (info.videoTracks && info.videoTracks.length) {
    const [videoTrack] = info.videoTracks;
    const { samples_duration: totalSampleDuration, nb_samples: sampleCount } = videoTrack;

    const sampleDuration = totalSampleDuration / sampleCount;
    console.log(`Acceptable Sample Durations: 1000, 1001 -- Actual Sample Duration: ${sampleDuration}`)
  }
}

export function logTimescale(info: Mp4BoxInfo) {
  if (info.videoTracks && info.videoTracks.length) {
    const [videoTrack] = info.videoTracks;
    const { timescale } = videoTrack;

    console.log(`Acceptable Timescales: 30000, 60000 -- Actual Timescale: ${timescale}`);
  }
}

export function validateFps(info: Mp4BoxInfo, updateError: UpdateError) {
  const supportedFramerates = [24, 25, 30, 50, 60];

  const [videoTrack] = info.videoTracks;
  const { timescale, samples_duration: totalSamplesDuration, nb_samples: nbSamples } = videoTrack;
  const sampleDuration = totalSamplesDuration / nbSamples;

  const fps = timescale / sampleDuration;

  if (fps - Math.floor(fps) === 0) {
    // Integer framerate must be exactly equal to a supported framerate

    console.log(`Supported Integer FPS: 24, 25, 30, 50, 60 -- Actual FPS: ${fps}`);

    if (supportedFramerates.includes(fps)) {
      updateError(VALIDATIONS.INVALID_FPS, false);
      return;
    }
  } else {
    // Non-integer framerate requirements:
    // 1. sampleDuration is an integer multiple, N, of 1001
    // 2. timescale is an integer multiple, M, of a supported framerate * 1000
    // 3. N == M

    const sampleDurationMultiple = sampleDuration / 1001;
    console.log(`Supported Non-Integer FPS: 24000/1001, 30000/1001, 60000/1001 -- Actual FPS: ${timescale / sampleDurationMultiple}/1001`);

    if (sampleDuration % 1001 === 0 && supportedFramerates.includes(timescale / sampleDurationMultiple / 1000)) {
      updateError(VALIDATIONS.INVALID_FPS, false);
      return;
    }
  }

  updateError(VALIDATIONS.INVALID_FPS);
}

export function validateDimensions(info: Mp4BoxInfo, updateError: UpdateError) {
  if (info.videoTracks && info.videoTracks.length > 0) {
    const { height, width } = info.videoTracks[0].video;
    console.log('Expected Aspect Ratio: 1.77 repeating');
    console.log(`Aspect Ratio: ${width / height} width: ${width} height: ${height}`);

    const requiredAspectRatio = 16 / 9;
    if (width / height !== requiredAspectRatio) {
      updateError(VALIDATIONS.INVALID_DIMENSIONS);
      return;
    }
    updateError(VALIDATIONS.INVALID_DIMENSIONS, false);
  }
}

export function validateRequiredField(value: string | undefined | null, errorKey: VALIDATIONS, updateError: UpdateError): boolean {
  if (!value?.toLowerCase().trim()) {
    updateError(errorKey);
    return false;
  }
  updateError(errorKey, false);
  return true;
}

export function validateFileType(value: string, updateError: UpdateError) {
  const splitValues: string[] = value.split('.');

  if (splitValues.length < 2) {
    updateError(VALIDATIONS.INVALID_FILE_TYPE);
    return;
  }

  const ext = splitValues[splitValues.length - 1];

  if (ext.toLowerCase() !== 'mp4') {
    updateError(VALIDATIONS.INVALID_FILE_TYPE);
    return;
  }

  updateError(VALIDATIONS.INVALID_FILE_TYPE, false);
}

export function validateSampleRate(info: Mp4BoxInfo, updateError: UpdateError) {
  if (!hasAudio(info)) {
    return;
  }

  const [audioTrack] = info.audioTracks;
  const sampleRate = audioTrack.audio.sample_rate;

  console.log(`Supported Audio Sample Rate 48000 Hz -- Actual Audio Sample Rate: ${sampleRate} Hz`);

  if (sampleRate !== AUDIO_SAMPLERATE) {
    updateError(VALIDATIONS.INVALID_AUDIO_SAMPLERATE);
    return;
  }
  updateError(VALIDATIONS.INVALID_AUDIO_SAMPLERATE, false);
}

export function validateUploadFileHasAudio(info: Mp4BoxInfo, updateError: UpdateError) {
  if (!hasAudio(info)) {
    updateError(VALIDATIONS.NO_AUDIO);
    return;
  }

  updateError(VALIDATIONS.NO_AUDIO, false);
}

export function calculateGop(mp4BoxFile: any, info: Mp4BoxInfo, updateError: UpdateError, dispatch: React.Dispatch<Action>, validate = true): void {
  // GOP is from true is_sync to next true is_sync
  // see samples tab: http://download.tsi.telecom-paristech.fr/gpac/mp4box.js/filereader.html

  let lastCts = 0;
  let largestTimeElapsed = 0;
  let largestMbps = 0;
  for (const track of info.videoTracks) {
    const samples: TrackSample[] = mp4BoxFile.getTrackSamplesInfo(track.id);
    let totalBytes = 0;
    for (const sample of samples) {
      const { cts, timescale, is_sync: isSync, size } = sample;

      if (!areDefined(cts, timescale, isSync)) {
        console.error(`CTS: ${cts}, TimeScale: ${timescale}, or isSync: ${isSync} is undefined`);
        updateError(VALIDATIONS.MAX_GOP)
        return;
      }

      /*
        GOP is from true isSync to next true isSync.
        We calculate time from the current isSync minus the time of the last
        to get the time of each interval.
        We also add totalBytes between isSyncs then use them to calculate mbps
        then set totalBytes to 0 for the next series of samples until the next true isSync
      */
      if (isSync) {
        const timeElapsed = (cts - lastCts) / timescale;

        if (timeElapsed > largestTimeElapsed) {
          largestTimeElapsed = timeElapsed;
        }
        if (timeElapsed > MAX_KEYFRAME_INTERVAL && validate) {
          updateError(VALIDATIONS.MAX_GOP);
          console.error(`Duration of GOP too large. Max: 3.2 seconds, Actual: ${timeElapsed} seconds`);
          return;
        }
        const mbps = totalBytes / timeElapsed / MB;
        if (mbps > largestMbps) {
          largestMbps = mbps;
        }
        if (mbps > MAX_FRAGMENT_MBPS && timeElapsed > 0 && validate) {
          updateError(VALIDATIONS.MAX_GOP);
          console.error(`Keyframe mbps is too large. Max: 24mbps, Actual: ${mbps.toFixed(2)} mbps`);
          return;
        }

        lastCts = cts;
        totalBytes = 0;
      }

      totalBytes += size;
    }
  }
  updateError(VALIDATIONS.MAX_GOP, false);
  dispatch({ type: ACTION_TYPE.GOP_SECONDS, payload: largestTimeElapsed });
  console.log(`Largest GOP duration is ${largestTimeElapsed} seconds`);
  console.log(`Keyframe mbps max is 24mbps --- Actual: ${(largestMbps).toFixed(2)} mbps`);
}

export function validateSubmit(state: EventUploadState, updateError: UpdateError) {
  // Event Profile
  const invalidEventProfile = validateRequiredField(state.eventProfile, VALIDATIONS.BLANK_EVENT_PROFILE, updateError);

  // Event Name
  const invalidEventName = validateRequiredField(state.eventName, VALIDATIONS.BLANK_EVENT_NAME, updateError);

  // Event Upload
  let invalidEventUpload = false;
  if (!state?.file?.toString().trim()) {
    invalidEventUpload = true;
    updateError(VALIDATIONS.NO_UPLOAD_FILE)
  }
  return invalidEventProfile || invalidEventName || invalidEventUpload;
}
